import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import FilmCard from "@/components/feature/FilmCard";
import Pagination from "@/components/feature/Pagination";
import { films, genres, years } from "@/mocks/films";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const ratingOptions = [
  { label: "全部评分", value: "all" },
  { label: "8.5 分以上", value: "8.5" },
  { label: "8.0 分以上", value: "8.0" },
  { label: "7.5 分以上", value: "7.5" },
];

const sortOptions = [
  { label: "按更新时间", value: "updated" },
  { label: "按热度", value: "views" },
  { label: "按评分", value: "rating" },
];

const PAGE_SIZE = 8;

const parseViews = (value: string) => {
  const numeric = Number.parseFloat(value);
  if (Number.isNaN(numeric)) return 0;
  return value.includes("万") ? numeric * 10000 : numeric;
};

const chipClass = (active: boolean) =>
  `whitespace-nowrap rounded-md border px-2.5 py-1.5 text-[12px] transition-colors ${
    active
      ? "border-primary-500 bg-primary-500 font-medium text-background-50"
      : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
  }`;

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const genre = searchParams.get("genre") ?? "all";
  const year = searchParams.get("year") ?? "all";
  const rating = searchParams.get("rating") ?? "all";
  const sort = searchParams.get("sort") ?? "updated";
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);

  const activeGenreName = genre === "all" ? "全部影视作品" : `${genre}影视作品`;

  useDocumentMeta({
    title: `${activeGenreName} - 韩国AV影视资源`,
    description: `浏览${activeGenreName}合集，支持按题材、年份、评分筛选，并提供按更新时间、热度、评分排序。收录 ${films.length} 部韩国电影与电视剧资料。`,
    keywords: `${activeGenreName},韩国AV影视资源,韩国电影,韩剧,${genre === "all" ? "影视分类" : genre}`,
  });

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all" || value === "" || (key === "sort" && value === "updated")) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const filtered = useMemo(() => {
    const list = films.filter((film) => {
      const genreMatch = genre === "all" || film.genres.includes(genre);
      const yearMatch = year === "all" || film.year === Number(year);
      const ratingMatch = rating === "all" || film.rating >= Number(rating);
      return genreMatch && yearMatch && ratingMatch;
    });

    return list.sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "views") return parseViews(b.views) - parseViews(a.views);
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
  }, [genre, year, rating, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const activeFilterCount =
    (genre !== "all" ? 1 : 0) + (year !== "all" ? 1 : 0) + (rating !== "all" ? 1 : 0);

  const goPage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) {
      next.delete("page");
    } else {
      next.set("page", String(nextPage));
    }
    setSearchParams(next);
    window.scrollTo({ top: 200, behavior: "smooth" });
  };

  const filterRows = (
    <>
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <span className="w-14 shrink-0 pt-1.5 text-[12px] font-medium text-foreground-500">题材</span>
        <div className="flex flex-1 flex-wrap gap-1.5">
          <button type="button" onClick={() => updateParam("genre", "all")} className={chipClass(genre === "all")}>
            全部
          </button>
          {genres.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => updateParam("genre", item)}
              className={chipClass(genre === item)}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-start gap-x-4 gap-y-2">
        <span className="w-14 shrink-0 pt-1.5 text-[12px] font-medium text-foreground-500">年份</span>
        <div className="flex flex-1 flex-wrap gap-1.5">
          <button type="button" onClick={() => updateParam("year", "all")} className={chipClass(year === "all")}>
            全部
          </button>
          {years.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => updateParam("year", String(item))}
              className={chipClass(year === String(item))}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-start gap-x-4 gap-y-2">
        <span className="w-14 shrink-0 pt-1.5 text-[12px] font-medium text-foreground-500">评分</span>
        <div className="flex flex-1 flex-wrap gap-1.5">
          {ratingOptions.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => updateParam("rating", item.value)}
              className={chipClass(rating === item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="mx-auto w-full max-w-[1280px] flex-1 px-4 pb-4 pt-6 md:px-6 lg:px-8">
        <nav aria-label="面包屑导航" className="flex items-center gap-1.5 text-[12px] text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <Link to="/category" className="transition-colors hover:text-primary-700">
            影视分类
          </Link>
          {genre !== "all" && (
            <>
              <i className="ri-arrow-right-s-line text-foreground-300" />
              <span className="text-foreground-800">{genre}</span>
            </>
          )}
        </nav>

        <div className="mt-4 flex flex-col gap-4 rounded-lg border border-background-200/90 bg-background-100 px-5 py-5 md:flex-row md:items-end md:justify-between md:px-6 md:py-6">
          <div>
            <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-accent-700">Film Category</span>
            <h1 className="mt-1.5 font-heading text-[26px] font-semibold leading-tight text-foreground-950 md:text-[30px]">
              {activeGenreName}
            </h1>
            <p className="mt-2 max-w-2xl text-[13px] leading-6 text-foreground-600">
              共 {filtered.length} 部作品符合条件的资料。可按题材、年份与评分组合筛选，切换排序查看不同维度的片单。
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="sort-select" className="whitespace-nowrap text-[12px] text-foreground-500">
              排序
            </label>
            <select
              id="sort-select"
              value={sort}
              onChange={(event) => updateParam("sort", event.target.value)}
              className="h-9 rounded-md border border-background-300 bg-background-50 px-3 text-[12px] text-foreground-800 outline-none focus:border-primary-400"
            >
              {sortOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-5 rounded-lg border border-background-200/90 bg-background-50 p-4 md:p-5">
          <div className="mb-3 flex items-center justify-between md:mb-4">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded bg-secondary-100 text-secondary-700">
                <i className="ri-filter-3-line text-xs" />
              </span>
              <h2 className="text-[13px] font-semibold text-foreground-900">筛选条件</h2>
              {activeFilterCount > 0 && (
                <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-medium text-accent-800">
                  {activeFilterCount} 项已选
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={resetFilters}
                className="hidden whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-[11px] text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-700 sm:inline-flex"
              >
                重置筛选
              </button>
              <button
                type="button"
                onClick={() => setFiltersOpen((prev) => !prev)}
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-[11px] text-foreground-600 md:hidden"
              >
                {filtersOpen ? "收起" : "展开筛选"}
                <i className={filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
              </button>
            </div>
          </div>
          <div className={`${filtersOpen ? "block" : "hidden"} md:block`}>
            {filterRows}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-[12px] text-foreground-500">
            共 <span className="font-medium text-foreground-900">{filtered.length}</span> 部作品
          </p>
          <p className="text-[11px] text-foreground-400">资源索引演示 · 不提供播放与下载</p>
        </div>

        {visible.length === 0 ? (
          <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-20 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-compass-3-line text-xl" />
            </span>
            <h3 className="mt-4 font-heading text-base font-semibold text-foreground-900">未找到符合条件的影视作品</h3>
            <p className="mt-1.5 max-w-sm text-[12px] leading-6 text-foreground-500">
              试着减少筛选条件，或者换一个题材看看。也可以直接浏览全部作品。
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-[12px] font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line text-sm" />
              重置筛选
            </button>
          </div>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
              {visible.map((film) => (
                <FilmCard key={film.id} film={film} />
              ))}
            </div>
            <Pagination page={currentPage} totalPages={totalPages} onChange={goPage} />
          </>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}